/** 与 ai-service 创建项目结构化草稿中的占位文案保持一致 */
const PROJECT_NAME_PLACEHOLDERS = new Set(['待确认项目名称', '项目名称待定', '未命名项目']);

function pickStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function isUsableProjectName(name: string): boolean {
  const value = name.trim();
  if (!value || PROJECT_NAME_PLACEHOLDERS.has(value)) return false;
  if (/^(一个|这个|那个|某|整|各)?项目$/.test(value)) return false;
  if (/^(帮我)?(创建|新建|新增|立项|做)/.test(value)) return false;
  return true;
}

/**
 * 只要取得真实项目名称即可进入「创建项目」页；其它字段在创建页继续补充。
 */
export function isStructuredProjectCreateReady(result: unknown): boolean {
  if (!result || typeof result !== 'object') return false;
  const o = result as Record<string, unknown>;
  return isUsableProjectName(pickStr(o, ['projectName', 'project_name', 'name']));
}

/** 用于对话内提示用户还需补充哪些信息（中文短句） */
export function getCreateProjectMissingHints(result: unknown): string[] {
  if (!result || typeof result !== 'object') return ['项目名称'];
  const o = result as Record<string, unknown>;
  const name = pickStr(o, ['projectName', 'project_name', 'name']);
  if (!isUsableProjectName(name)) {
    return ['具体项目名称（不要用「待确认项目名称」这类占位）'];
  }
  return [];
}

const LEGACY_CREATE_PROJECT_GAP_MARKER = '\n\n——\n【提示】还不能进入「分屏创建项目」页面';
const CREATE_PROJECT_GAP_MARKER = '\n\n——\n【提示】还不能进入「创建项目」页面';

/** 从已保存的助手正文中移除「创建项目」缺口提示（历史数据可能仍含旧「分屏」文案） */
export function stripCreateProjectGapNotice(text: string): string {
  let s = String(text ?? '');
  for (const marker of [CREATE_PROJECT_GAP_MARKER, LEGACY_CREATE_PROJECT_GAP_MARKER]) {
    const i = s.indexOf(marker);
    if (i >= 0) s = s.slice(0, i).trimEnd();
  }
  return s;
}

/** 与 formatProjectCreateNoRouteAccessNotice 共用前缀，便于去重与历史正文清理 */
const LEGACY_PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER = '\n\n——\n【提示】当前账号无权使用「分屏创建项目」';
const PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER = '\n\n——\n【提示】当前账号无权使用「创建项目」';

/** 表单/弹窗等 UI 用的无创建权限提示（与后端 POST /api/projects 角色约束一致） */
export const PROJECT_CREATE_NO_ACCESS_MESSAGE =
  '当前账号无权创建项目：需具备超级管理员、租户管理员、SE 或项目经理角色，请联系管理员配置后再试';

/** 无创建项目角色时追加在助手回复末尾（与后端 POST /api/projects 角色约束一致） */
export function formatProjectCreateNoRouteAccessNotice(): string {
  return `${PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER}：需具备 **超级管理员、管理员、系统工程师或项目经理** 角色（与系统创建项目接口一致）。请联系管理员配置后再试。`;
}

/** AI 对话识别到创建意图但账号无权限时的整段助手回复（含可去重标记） */
export function formatProjectCreateNoAccessAssistantReply(): string {
  return `很抱歉，当前账号没有创建项目的权限。${formatProjectCreateNoRouteAccessNotice()}`;
}

export function stripProjectCreateNoRouteAccessNotice(text: string): string {
  let s = String(text ?? '');
  for (const marker of [PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER, LEGACY_PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER]) {
    const i = s.indexOf(marker);
    if (i >= 0) s = s.slice(0, i).trimEnd();
  }
  return s;
}

/** 是否已包含「无创建项目权限」提示（避免重复追加） */
export function hasProjectCreateNoRouteAccessNotice(text: string): boolean {
  const t = String(text ?? '');
  return t.includes(PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER) || t.includes(LEGACY_PROJECT_CREATE_NO_ROUTE_ACCESS_MARKER);
}

/** 追加在助手回复末尾，明确说明还不能进入创建项目页 */
export function formatCreateProjectGapNotice(result: unknown): string {
  if (!result || typeof result !== 'object') {
    return `${CREATE_PROJECT_GAP_MARKER}：请继续补充 **项目名称**；客户名称、整体交付日期、项目目标和项目类型可进入创建页后继续完善。`;
  }
  const parts = getCreateProjectMissingHints(result);
  if (!parts.length) return '';
  return `${CREATE_PROJECT_GAP_MARKER}，请先完善：${parts.join('；')}。客户名称、整体交付日期、项目目标和项目类型可进入创建页后继续完善。`;
}
